import {
  ReadOnlyPdfPreview,
  useReadOnlyPdfFallback,
  useSharedPdfViewerGate,
} from '@liscio/common';

import PDFViewerErrorBoundary from 'common/PDFViewerErrorBoundary/PDFViewerErrorBoundary';
import { reportPdfViewerError } from 'common/PDFViewerErrorBoundary/reportPdfViewerError';

import PspdfkitDocumentPDFViewer, {
  type PspdfkitDocumentPDFViewerProps,
} from './PspdfkitDocumentPDFViewer';
import SharedDocumentPDFViewer from './SharedDocumentPDFViewer';

type DocumentPDFViewerProps = PspdfkitDocumentPDFViewerProps & {
  /** Current display name for the shared viewer, which reads it live. PSPDFKit
   * keeps `fileName` and receives renames through its instance. */
  title: string;
};

const readOnlyFallbackStyle: React.CSSProperties = {
  width: '100%',
  height: 'inherit',
};

/**
 * Thin switch between the legacy Files viewers. The gate is held for this mount,
 * so a flag change can't swap the viewer and lose a note draft. With the flag on,
 * clients the gate turns away get a read-only preview. Nothing claims DocumentShow's
 * download event there, so its header Download navigates to the attachment URL.
 */
export default function DocumentPDFViewer({
  title,
  ...props
}: DocumentPDFViewerProps) {
  const sharedViewer = useSharedPdfViewerGate();
  const readOnlyFallback = useReadOnlyPdfFallback();
  if (sharedViewer) {
    return <SharedDocumentPDFViewer {...props} fileName={title} />;
  }
  if (readOnlyFallback) {
    return (
      <div style={readOnlyFallbackStyle}>
        <PDFViewerErrorBoundary
          key={props.url}
          operation="loadReadOnlyPdfPreview"
        >
          <ReadOnlyPdfPreview
            url={props.url}
            title={title}
            showDownload={false}
            onError={reportPdfViewerError}
          />
        </PDFViewerErrorBoundary>
      </div>
    );
  }
  return <PspdfkitDocumentPDFViewer {...props} />;
}
